import { useQuery } from '@tanstack/react-query';

import { EntryTypeStatus, Optional } from 'chaika-frontend-types/frontend-cms';

import { queryClient } from '@store/queryClient';
import { assertIsDefined } from '@utils/assertIsDefined';

import { EntryTypeDraft } from '../types';
import { editEntryTypeQueryOptions } from '../queries/editEntryTypeQueries';
import { useSaveEntryTypeDraft } from '../queries/editEntryTypeMutations';

const DRAFT_REQUIRED_MESSAGE = 'Entry type version must be loaded before it is edited';

type PatchDraft = (draft: EntryTypeDraft) => EntryTypeDraft;

interface Params {
    entryTypeId: string;
    version: number;
}

interface Actions {
    updateDraft(patch: PatchDraft): void;
    saveDraft(): Promise<void>;
    updateAndSaveDraft(patch: PatchDraft): Promise<void>;
}

interface Data extends Actions {
    entryType: Optional<EntryTypeDraft>;
    canEdit: boolean;
    isLoading: boolean;
}

export function useEntryTypeDraft({ entryTypeId, version }: Params): Data {
    const { data: entryType, isLoading } = useQuery(editEntryTypeQueryOptions.detail(entryTypeId, version));
    const actions = useEntryTypeDraftActions({ entryTypeId, version });

    return {
        ...actions,
        entryType,
        canEdit: entryType?.status === EntryTypeStatus.Draft,
        isLoading,
    };
}

export function useEntryTypeDraftActions({ entryTypeId, version }: Params): Actions {
    const { queryKey } = editEntryTypeQueryOptions.detail(entryTypeId, version);
    const saveMutation = useSaveEntryTypeDraft();

    const updateDraft = (patch: PatchDraft): void => {
        queryClient.setQueryData<EntryTypeDraft>(queryKey, current => {
            assertIsDefined(current, DRAFT_REQUIRED_MESSAGE);

            return patch(current);
        });
    };

    const saveDraft = async (): Promise<void> => {
        const current = queryClient.getQueryData<EntryTypeDraft>(queryKey);

        assertIsDefined(current, DRAFT_REQUIRED_MESSAGE);

        await saveMutation.mutateAsync(current);
    };

    const updateAndSaveDraft = async (patch: PatchDraft): Promise<void> => {
        updateDraft(patch);

        await saveDraft();
    };

    return { updateDraft, saveDraft, updateAndSaveDraft };
}
